/**
 * ThesaurusSynonymView - Synonym exploration interface for selected thesaurus senses
 *
 * Displays synonym categories (Core, Near, Phrases, Metaphor Bridges) with
 * collection affordances and provenance tracking. Provides "Collect & Explore Rhymes"
 * workflow and breadcrumb navigation.
 *
 * Scope 06: Uses collectWord for dedup/reactivation instead of raw createWord+addWord.
 */

import type { FC } from 'react'
import { SynonymSection } from './SynonymSection'
import { useWordActions } from '../../hooks/useWordActions'
import type { ThesaurusSynonymResponse } from '../../types/wordTools'

interface ThesaurusSynonymViewProps {
  /** Synonym results from API */
  synonymResults: ThesaurusSynonymResponse
  /** Original search word */
  sourceWord: string
  /** Called when user clicks Back button */
  onGoBack: () => void
  /** Called when user clicks "Collect & Explore Rhymes" */
  onCollectAndExploreRhymes?: (word: string) => void
}

/**
 * Synonym view showing categorized synonyms for selected thesaurus senses.
 *
 * Features:
 * - Breadcrumb navigation (word → sense)
 * - Back button (works with ESC key)
 * - Four synonym category sections
 * - Collection with thesaurus provenance
 * - "Collect & Explore Rhymes" workflow integration
 */
export const ThesaurusSynonymView: FC<ThesaurusSynonymViewProps> = ({
  synonymResults,
  sourceWord,
  onGoBack,
  onCollectAndExploreRhymes
}) => {
  // Use centralized collectWord for dedup/reactivation
  const { collectWord } = useWordActions()

  // Handle "Collect & Explore Rhymes" button
  const handleCollectAndExploreRhymes = () => {
    // collectWord handles dedup — if word exists and is set_aside, reactivates it
    collectWord(sourceWord, {
      method: 'thesaurus_lookup',
      thesaurus_data: {
        source_word: sourceWord,
        index_entry: sourceWord,
        sense: 'exploration',
        category: 'direct_search'
      }
    })

    // Call parent callback to switch to rhymes tab
    if (onCollectAndExploreRhymes) {
      onCollectAndExploreRhymes(sourceWord)
    }
  }

  // Category descriptions
  const categoryDescriptions = {
    coreSynonyms: 'Strongest semantic matches - near-identical meaning',
    nearSynonyms: 'Close but not identical meaning - adds nuance',
    phrasesCollocations: 'Common expressions and word pairings',
    metaphorBridges: 'Creative connections and figurative associations'
  }

  return (
    <div data-testid="thesaurus-synonym-view" className="p-4 space-y-4">
      {/* Header with breadcrumb and back button */}
      <div className="space-y-3">
        <button
          data-testid="thesaurus-back-button"
          onClick={onGoBack}
          className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to senses
        </button>

        {/* Breadcrumb */}
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{synonymResults.word}</span>
          <span className="mx-2">→</span>
          <span>
            {synonymResults.selectedSenses.length} sense
            {synonymResults.selectedSenses.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Collect & Explore Rhymes button */}
        <button
          data-testid="collect-and-explore-rhymes-button"
          onClick={handleCollectAndExploreRhymes}
          className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Collect & Explore Rhymes
        </button>
      </div>

      {/* Synonym sections for each selected sense */}
      <div className="space-y-4">
        {synonymResults.selectedSenses.map((sense, index) => (
          <div key={index} className="space-y-3">
            {/* Sense label */}
            <div className="text-xs font-medium text-gray-700 bg-gray-100 rounded px-2 py-1">
              {sense.senseLabel}
            </div>

            {/* Category sections */}
            <div className="space-y-3">
              <SynonymSection
                title="Core Synonyms"
                description={categoryDescriptions.coreSynonyms}
                synonyms={sense.coreSynonyms}
                sourceWord={sourceWord}
                senseLabel={sense.senseLabel}
                category="Core Synonyms"
              />

              <SynonymSection
                title="Near Synonyms"
                description={categoryDescriptions.nearSynonyms}
                synonyms={sense.nearSynonyms}
                sourceWord={sourceWord}
                senseLabel={sense.senseLabel}
                category="Near Synonyms"
              />

              <SynonymSection
                title="Phrases & Collocations"
                description={categoryDescriptions.phrasesCollocations}
                synonyms={sense.phrasesCollocations}
                sourceWord={sourceWord}
                senseLabel={sense.senseLabel}
                category="Phrases & Collocations"
              />

              <SynonymSection
                title="Metaphor Bridges"
                description={categoryDescriptions.metaphorBridges}
                synonyms={sense.metaphorBridges}
                sourceWord={sourceWord}
                senseLabel={sense.senseLabel}
                category="Metaphor Bridges"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
